// Maps the Salesforce FinancialProfileService JSON shape BACK into the
// React app's store shape. Inverse of toSalesforce.js — used when the app
// loads with a ?pqid= URL param and hydrates from an existing record.

import { genUid } from './uid.js';
import { parseLog } from './planningLog.js';
import { estateDocCodes } from './estateDocs.js';

const str = v => (v === undefined || v === null ? '' : String(v));
const rowUid = r => (r && r.uid ? String(r.uid) : genUid());

// The professional-relationship roles the app pre-seeds (Financial Advisor,
// Attorney, Accountant). DataTable stamps `_seeded: ['role']` on these starter
// rows so completion doesn't count an untouched default as progress — but that
// marker is internal (`_`-prefixed) and gets stripped on the Salesforce
// round-trip. Without it, a saved-but-untouched default role reads as real
// input and inflates the section (and overall) percentage.
//
// Explicit legacy list, NOT derived from starterRows any more. The section
// stopped pre-seeding these three rows on 2026-07-24 (Jake: a client with no
// attorney shouldn't show an empty Attorney line on the client-facing view),
// but records saved BEFORE that still contain them. Deriving this set from the
// now-empty starterRows would make those untouched rows start counting toward
// completion on reload, so the role names are pinned here instead.
const SEEDED_PRO_ROLES = new Set(['Financial Advisor', 'Attorney', 'Accountant']);
function mapFamilyChildren(sfChildren) {
  const children = [];
  const grandchildren = [];
  (Array.isArray(sfChildren) ? sfChildren : []).forEach(c => {
    const row = { _uid: rowUid(c), name: str(c?.name), age: str(c?.age), notes: str(c?.notes) };
    if (c?.relationship === 'grand-children') grandchildren.push(row);
    else children.push(row);
  });
  return { children, grandchildren };
}

// The handoff snapshot arrives as one JSON string {at, by, data} (see
// toSalesforce.serializeArchive). Anything malformed reads as "no snapshot" —
// a corrupt archive must never block loading the live record it rides on.
function parseArchive(raw) {
  const text = str(raw);
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || !str(parsed.at)) return null;
    return {
      at: str(parsed.at),
      by: str(parsed.by),
      data: parsed.data && typeof parsed.data === 'object' ? parsed.data : {},
    };
  } catch {
    return null;
  }
}

// Must match OTHER_BUSINESS_KEY in toSalesforce.js: the marker in the external
// key of an employment record that is one of a person's other businesses rather
// than their main job.
const OTHER_BUSINESS_KEY = '-emp-biz-';

// `names` are the first names the Owner dropdown offers ({ client, spouse }), so
// a reloaded business shows the same owner the advisor picked.
function mapEmployment(sfEmployment, names = {}) {
  const out = {};
  const others = [];
  (Array.isArray(sfEmployment) ? sfEmployment : []).forEach(e => {
    // Salesforce may return employmentInfoFor as a label ("Spouse") rather than
    // the code we send ("spouse"); match case-insensitively so the spouse record
    // doesn't fall into client1 (which also corrupts the saved external key).
    const key = String(e?.employmentInfoFor || '').toLowerCase().includes('spouse') ? 'client2' : 'client1';
    const extKey = String(e?.uid || '');
    if (extKey.includes(OTHER_BUSINESS_KEY)) {
      others.push({
        // The row's own id is the part after the marker, so the next save
        // writes back to the same record instead of creating a new one.
        _uid: extKey.split(OTHER_BUSINESS_KEY).pop() || genUid(),
        owner: (key === 'client2' ? names.spouse : names.client) || '',
        name: str(e?.businessName) || str(e?.employer),
        role: str(e?.jobTitle),
        typeOfBusiness: str(e?.typeOfBusiness),
        numOfYears: str(e?.numOfYears),
      });
      return;
    }
    out[key] = {
      _uid: rowUid(e),
      status: str(e?.employmentStatus) || 'Employed',
      jobTitle: str(e?.jobTitle),
      employer: str(e?.employer),
      // Store under `numOfYears` — the key the Employment UI binds to and that
      // toSalesforce reads. Writing `yearsEmployed` here left the field blank on
      // reload (the UI never read that key).
      numOfYears: str(e?.numOfYears),
      retirementDate: str(e?.retirementDate),
      retirementAge: str(e?.retirementAge),
      typeOfBusiness: str(e?.typeOfBusiness),
      lastJob: str(e?.lastJobTitle),
      lastEmployer: str(e?.lastEmployer),
      yearsWorked: str(e?.lastNumOfYears),
    };
  });
  if (others.length) out.otherBusinesses = others;
  return out;
}

function mapIncomes(sfIncomes) {
  const income = { employment: [], socialSecurity: [], pension: [], other: [] };
  (Array.isArray(sfIncomes) ? sfIncomes : []).forEach(r => {
    const base = {
      _uid: rowUid(r),
      owner: str(r?.owner),
      description: str(r?.description),
      annualAmount: str(r?.annualAmount),
      cola: str(r?.colaPercentage),
      // Full/partial-year indicator (shared across all income types). Returns
      // once its Salesforce field lands (Srivani).
      yearType: str(r?.yearType),
      notes: str(r?.notes),
    };
    // Salesforce returns the income type as a display label ("Social Security")
    // rather than the code we send ("socialSecurity"), so normalize before
    // matching — otherwise every row falls through to employment on reload.
    const t = String(r?.type || '').toLowerCase().replace(/[^a-z]/g, '');
    if (t.includes('socialsecurity')) {
      income.socialSecurity.push({
        ...base,
        startingAge: str(r?.startingAge),
        fullRetAge: str(r?.fullRetirementAge),
      });
    } else if (t.includes('pension')) {
      income.pension.push({
        ...base,
        startDate: str(r?.startDate || r?.retirementDate),
        // startAge returns once its Salesforce field lands (Srivani).
        startAge: str(r?.startAge),
        survivorBenefit: str(r?.survivorBenefit),
      });
    } else if (t.includes('other')) {
      income.other.push({
        ...base,
        startDate: str(r?.startDate || r?.retirementDate),
        endDate: str(r?.endDate),
      });
    } else {
      income.employment.push({ ...base, retirementDate: str(r?.retirementDate) });
    }
  });
  return income;
}

function mapTaxesAndExpenses(sfRows, fp) {
  const te = {
    // Read these from the Financial Profile scalars (where toSalesforce now
    // sends them). They used to arrive as "Tax Information" rows, which are no
    // longer sent — so without reading the scalars, advisor-entered Taxable
    // Income and Federal Tax silently vanished on reload. The row handling
    // below still overwrites these if a legacy record returns the old rows.
    taxableIncome: str(fp?.taxableIncome),
    federalTax: str(fp?.federalTax),
    stateTax: str(fp?.stateTax),
    ficaTax: str(fp?.ficaTax),
    standardItemDeduction: str(fp?.standardItemDeduction),
    // Return once their Salesforce fields land (Srivani).
    taxBasis: str(fp?.taxBasis),
    deductionType: str(fp?.deductionType),
    capLossCarryForward: str(fp?.capLossCarryForward),
    livingExpenses: str(fp?.livingExpenses),
    livingExpensesNotes: str(fp?.livingExpensesNotes),
    expenses: [],
  };
  (Array.isArray(sfRows) ? sfRows : []).forEach(r => {
    const desc = str(r?.description);
    if (r?.category === 'Tax Information') {
      if (desc === 'Taxable Income') te.taxableIncome = str(r?.amount);
      else if (desc === 'Federal') te.federalTax = str(r?.amount);
      else if (desc === 'State') te.stateTax = str(r?.amount);
      else if (desc === 'FICA') te.ficaTax = str(r?.amount);
      return;
    }
    // Annual expense rows
    te.expenses.push({
      _uid: rowUid(r),
      category: str(r?.category),
      subCategory: str(r?.subCategory),
      description: desc,
      amount: str(r?.amount),
      startDate: str(r?.startDate),
      endDate: str(r?.endDate),
      cola: str(r?.colaPercentage),
      notes: str(r?.notes),
    });
  });
  return te;
}

// Group the flat SF assets[] back into per-category store arrays.
function mapAssets(sfAssets) {
  const assets = {
    realEstate: [],
    businessOther: [],
    taxDeferred: [],
    roth: [],
    taxable: [],
    cashCd: [],
    plan529: [],
    hsa: [],
  };
  (Array.isArray(sfAssets) ? sfAssets : []).forEach(a => {
    const cat = str(a?.category);
    const sub = str(a?.subCategory);
    // notes rides on `base` so every asset category picks up the per-row note.
    const base = { _uid: rowUid(a), notes: str(a?.notes) };
    if (cat === 'Real Estate') {
      assets.realEstate.push({
        ...base,
        _autoKey:
          sub === 'Primary Residence' ? 'ownHome'
          : sub === 'Secondary Residence' ? 'secondaryHome'
          : sub === 'Additional Real Estate' ? 'additionalRE'
          : undefined,
        description: str(a?.description),
        marketValue: str(a?.marketValue),
        remainingLoan: str(a?.remainingLoan),
        // Real estate sends its rate under `interestRate` (intRate is null), so
        // read that back. Fall back to intRate for any legacy record that
        // stored it the other way.
        interestRate: str(a?.interestRate || a?.intRate),
        term: str(a?.term),
        payment: str(a?.paymentPAndI),
        yearAcquired: str(a?.yearAcquired),
        costBasis: str(a?.costBasis),
        ownership: str(a?.ownership),
        incomeEBT: str(a?.incomeEbit),
      });
    } else if (cat === 'Business and Other') {
      assets.businessOther.push({
        ...base,
        description: str(a?.description),
        marketValue: str(a?.marketValue),
        costBasis: str(a?.costBasis),
        ownership: str(a?.ownership),
      });
    } else if (cat === 'Investment Accounts') {
      const row = {
        ...base,
        custodian: str(a?.custodian),
        marketValue: str(a?.marketValue),
        personalAdditions: str(a?.personalAdditions),
        // Only a tick is carried: an unticked box stays blank like any empty
        // field, so it never reads as data or as a change in the planning log.
        ...(a?.maxContribution === true ? { maxContribution: true } : {}),
        companyMatch: str(a?.companyMatch),
        type: str(a?.type),
        ownership: str(a?.ownership),
        beneficiary: str(a?.beneficiary),
        costBasis: str(a?.costBasis),
        variableFixed: str(a?.variableFixed),
        issueDate: str(a?.issueDate),
        yearEstablished: str(a?.yearEstablished),
      };
      // Bucket by subCategory when present; otherwise fall back to the account
      // type. Salesforce does NOT round-trip our subCategory for investment
      // accounts (it returns null), so without this fallback every 401(k)/Roth/
      // taxable account would be dropped on load and net worth would collapse.
      const t = str(a?.type).toLowerCase();
      let bucket;
      if (sub === 'Tax-Deferred Retirement') bucket = 'taxDeferred';
      else if (sub === 'Tax-Free Roth') bucket = 'roth';
      else if (sub === 'Taxable Non-Retirement') bucket = 'taxable';
      else if (/roth/.test(t)) bucket = 'roth';
      else if (/401|403|457|\bira\b|sep|simple|pension|deferred|annuit/.test(t)) bucket = 'taxDeferred';
      else bucket = 'taxable';
      assets[bucket].push(row);
    } else if (cat === "Cash & CD's") {
      assets.cashCd.push({
        ...base,
        description: str(a?.description),
        marketValue: str(a?.marketValue),
        interestRate: str(a?.interestRate),
        ownership: str(a?.ownership),
      });
    } else if (cat === '529 Plans') {
      assets.plan529.push({
        ...base,
        description: str(a?.description),
        marketValue: str(a?.marketValue),
        interestRate: str(a?.interestRate),
        ownership: str(a?.ownership),
        // Custodian (plan provider) rides the existing asset `custodian` field
        // that Tax-Deferred/Roth/HSA already use, so it round-trips with no new
        // Salesforce field.
        custodian: str(a?.custodian),
        // FBO + sponsoring state (529 redesign, UAT 7/8). beneficiary already
        // round-trips; state returns once its SF field lands (Srivani).
        beneficiary: str(a?.beneficiary),
        state: str(a?.state),
      });
    } else if (cat === 'HSA Accounts') {
      assets.hsa.push({
        ...base,
        // HSA now mirrors the Roth column set (custodian/additions/match/type/
        // beneficiary). Legacy rows saved under the old Description column fall
        // back into Custodian so they stay visible; the next save migrates them.
        custodian: str(a?.custodian || a?.description),
        description: str(a?.description),
        marketValue: str(a?.marketValue),
        personalAdditions: str(a?.personalAdditions),
        ...(a?.maxContribution === true ? { maxContribution: true } : {}),
        companyMatch: str(a?.companyMatch),
        type: str(a?.type),
        interestRate: str(a?.interestRate),
        ownership: str(a?.ownership),
        beneficiary: str(a?.beneficiary),
      });
    }
  });
  return assets;
}

export function fromSalesforce(sf = {}) {
  const client = sf.client || {};
  const spouse = sf.spouse || {};
  const contact = sf.contactInfo || {};
  const fp = sf.financialProfile || {};
  const fpFamily = fp.family || {};
  const goals = fp.goals || {};
  const estate = fp.estatePlan || {};

  const { children, grandchildren } = mapFamilyChildren(fpFamily.children);
  const assets = mapAssets(fp.assets);
  const ownerName = p => str(p?.firstName) || str(p?.lastName);
  const employment = mapEmployment(fp.employmentInfo, { client: ownerName(client), spouse: ownerName(spouse) });
  const income = mapIncomes(fp.incomes);
  // (The Annual Earnings field and its back-fill were removed 2026-07-14. The
  // employment salary is now a normal editable amount on the Income row, so it
  // round-trips directly like any other income amount, no reflection needed.)

  const hasSpouse = !!(str(spouse.firstName) || str(spouse.lastName));
  const hasChildren = children.length > 0 || grandchildren.length > 0;
  const archive = parseArchive(fp.pqSnapshot);
  const planningLog = parseLog(fp.planningChangeLog);

  // Derive asset checkbox state from which categories returned rows.
  const assetChecks = {
    ownHome: assets.realEstate.some(r => r._autoKey === 'ownHome'),
    secondaryHome: assets.realEstate.some(r => r._autoKey === 'secondaryHome'),
    additionalRE: assets.realEstate.some(r => r._autoKey === 'additionalRE'),
    taxDeferred: assets.taxDeferred.length > 0,
    roth: assets.roth.length > 0,
    taxable: assets.taxable.length > 0,
    cashCd: assets.cashCd.length > 0,
    plan529: assets.plan529.length > 0,
    hsa: assets.hsa.length > 0,
    // Returns once Is_Renting__c exists on Financial_Profile__c (Srivani).
    renting: !!fp.isRenting,
  };

  return {
    _uid: str(sf.pqid) || str(fp.uid) || genUid(),
    _flags: { hasSpouse, hasChildren },
    _assetChecks: assetChecks,
    // The handoff snapshot (the PQ as the advisor submitted it), parsed back
    // from its single Long Text Area. Absent until that field exists in
    // Salesforce, and absent on records never handed off — both read as
    // "no snapshot yet", which the planning view renders honestly.
    ...(archive ? { pqArchive: archive } : {}),
    // The planning change log, same single-field pattern as the snapshot.
    ...(planningLog ? { planningLog } : {}),
    family: {
      client1FirstName: str(client.firstName),
      client1LastName: str(client.lastName),
      client1Nickname: str(client.nickName),
      client1DOB: str(client.dateOfBirth),
      client1Age: str(client.age),
      client2FirstName: str(spouse.firstName),
      client2LastName: str(spouse.lastName),
      client2Nickname: str(spouse.nickName),
      client2DOB: str(spouse.dateOfBirth),
      client2Age: str(spouse.age),
      yearsMarried: str(spouse.yearsMarried),
      numChildren: str(fpFamily.noOfChildren),
      numGrandchildren: str(fpFamily.noOfGrandchildren),
      additionalFamilyInfo: str(fpFamily.additionalFamilyInformation),
      children,
      grandchildren,
    },
    contact: {
      address1: str(contact.streetAddress),
      address2: str(contact.addressLine2),
      city: str(contact.city),
      state: str(contact.state),
      zip: str(contact.zipCode),
      homePhone: str(contact.clientHomePhone),
      client1Cell: str(contact.clientMobilePhone),
      client1Email: str(contact.clientEmail),
      client2Cell: str(contact.spouseMobilePhone),
      client2Email: str(contact.spouseEmail),
      referredBy: str(contact.referredBy),
    },
    employment,
    goals: {
      clientGoals: str(goals.clientGoals),
      additionalNotes: str(goals.additionalNotes),
      advisorNotes: str(goals.advisorNotes),
      // Comma-joined on the way in, and sometimes spelt out ("Financial
      // POA"); the app keeps the codes.
      estatePlan: estateDocCodes(estate.estatePlan),
      yearsEstablished: str(estate.yearsEstablished),
    },
    relationships: {
      professionals: (Array.isArray(fp.professionalRelationships)
        ? fp.professionalRelationships
        : []
      ).map(r => {
        const row = {
          _uid: rowUid(r),
          role: str(r?.role),
          name: str(r?.name),
          firm: str(r?.firm),
          city: str(r?.city),
          state: str(r?.state),
        };
        // A default role with nothing else filled is an untouched seeded row;
        // restore the marker so it doesn't count toward completion.
        const onlyRole = row.role && !row.name && !row.firm && !row.city && !row.state;
        if (onlyRole && SEEDED_PRO_ROLES.has(row.role)) row._seeded = ['role'];
        return row;
      }),
    },
    liabilities: {
      items: (Array.isArray(fp.liabilities) ? fp.liabilities : []).map(r => ({
        _uid: rowUid(r),
        description: str(r?.description),
        type: str(r?.type),
        payment: str(r?.paymentPAndI),
        amount: str(r?.amount),
        interestRate: str(r?.intRate),
        term: str(r?.termYears),
        notes: str(r?.notes),
      })),
    },
    insurance: {
      // Split back the same way estatePlan is. Absent until the Salesforce
      // field exists, which reads as "no markers" rather than an error.
      noneOf: str(fp.insuranceNoneOf)
        ? str(fp.insuranceNoneOf).split(',').map(x => x.trim()).filter(Boolean)
        : [],
      policies: (Array.isArray(fp.insurance) ? fp.insurance : []).map(r => ({
        _uid: rowUid(r),
        company: str(r?.company),
        type: str(r?.type),
        benefit: str(r?.deathDailyBenefit),
        insured: str(r?.insured),
        owner: str(r?.owner),
        policyDate: str(r?.policyDate),
        annualPremium: str(r?.annualPremium),
        cashValue: str(r?.cashValue),
        termYears: str(r?.termYears),
        beneficiary: str(r?.beneficiary),
        notes: str(r?.notes),
      })),
    },
    income,
    taxesExpenses: mapTaxesAndExpenses(fp.taxesAndExpenses, fp),
    // rentMonthly + additionalAccountInfo ride alongside the realEstate/
    // investment buckets mapAssets already returns. Each returns once its
    // Salesforce field exists (Monthly_Rent__c / Additional_Account_Info__c,
    // Srivani).
    assets: {
      ...assets,
      // Split back the same way insurance.noneOf is. Absent until the Salesforce
      // field exists, which reads as "no markers" rather than an error.
      noneOf: str(fp.investmentNoneOf)
        ? str(fp.investmentNoneOf).split(',').map(x => x.trim()).filter(Boolean)
        : [],
      rentMonthly: str(fp.monthlyRent),
      additionalAccountInfo: str(fp.additionalAccountInfo),
    },
  };
}
